import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import { useStableCallback } from '@main/shared/browser';
import {
  DEFAULT_IMAGE_DIMENSION_CONSTRAINTS,
  ImageDimensionConstraints,
  normalizeImageDimensions,
} from '@main/shared/utils/lexical/image-dimensions';
import { useNhostClient } from '@nhost/react';
import { COMMAND_PRIORITY_LOW, createCommand, LexicalCommand, PASTE_COMMAND } from 'lexical';
import { useEffect, useLayoutEffect, useMemo } from 'react';

import {
  setEditorImageConfig,
  setEditorImagePreviewUrl,
} from '../../wysiwyg/nodes/editor-image-config';
import { INSERT_IMAGE_COMMAND } from './image-plugin';

export type ImageUploadCommandPayload =
  | File[]
  | {
      files: File[];
      onComplete?(upload: Promise<void>): void;
    };

export const UPLOAD_IMAGE_COMMAND: LexicalCommand<ImageUploadCommandPayload> =
  createCommand('UPLOAD_IMAGE_COMMAND');

export type ImageUploadErrorReason = 'unsupported-type' | 'oversize' | 'validation' | 'upload';

export class ImageUploadError extends Error {
  constructor(readonly reason: ImageUploadErrorReason) {
    super(reason);
    this.name = 'ImageUploadError';
  }
}

type ImageUploadPluginProps = {
  acceptedMimeTypes?: readonly string[];
  maxFileSizeBytes?: number;
  dimensions?: ImageDimensionConstraints;
  resolveUrl?: (fileId: string) => Promise<string | null | undefined>;
  prepareFileForUpload?: (file: File) => Promise<File>;
  loadErrorMessage?: string;
  resizeLabel?: string;
  onError?: (error: Error, context: { fileName?: string }) => void;
};

export function ImageUploadPlugin({
  acceptedMimeTypes,
  maxFileSizeBytes,
  dimensions = DEFAULT_IMAGE_DIMENSION_CONSTRAINTS,
  resolveUrl,
  prepareFileForUpload,
  loadErrorMessage,
  resizeLabel,
  onError,
}: ImageUploadPluginProps): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const nhost = useNhostClient();
  const acceptedMimeTypesSet = useMemo(
    () => (acceptedMimeTypes ? new Set(acceptedMimeTypes) : undefined),
    [acceptedMimeTypes],
  );

  useLayoutEffect(() => {
    setEditorImageConfig(editor, {
      resolveUrl,
      dimensions,
      loadErrorMessage,
      resizeLabel,
    });

    return () => setEditorImageConfig(editor);
  }, [dimensions, editor, loadErrorMessage, resizeLabel, resolveUrl]);

  const uploadPreparedFile = useStableCallback(async (file: File) => {
    const { fileMetadata, error } = await nhost.storage.upload({ file });

    if (error || !fileMetadata) {
      throw new ImageUploadError('upload');
    }

    return fileMetadata;
  });

  const handleFiles = useStableCallback(async (files: File[]) => {
    const imageFiles = files.filter((file) => getImageFileMimeType(file)?.startsWith('image/'));
    if (imageFiles.length === 0) {
      return false;
    }

    for (const file of imageFiles) {
      try {
        const mimeType = getImageFileMimeType(file);
        if (!mimeType?.startsWith('image/')) {
          continue;
        }

        if (acceptedMimeTypesSet && !acceptedMimeTypesSet.has(mimeType)) {
          throw new ImageUploadError('unsupported-type');
        }

        if (maxFileSizeBytes !== undefined && file.size > maxFileSizeBytes) {
          throw new ImageUploadError('oversize');
        }

        const typedFile = file.type ? file : new File([file], file.name, { type: mimeType });
        const uploadableFile = prepareFileForUpload
          ? await prepareFileForUpload(typedFile)
          : typedFile;
        const res = await uploadPreparedFile(uploadableFile);
        const previewObjectUrl = URL.createObjectURL(uploadableFile);
        setEditorImagePreviewUrl(editor, res.id, previewObjectUrl);

        const { width, height } =
          uploadableFile.type === 'image/svg+xml'
            ? {}
            : normalizeImageDimensions(
                ...(await readImageDimensions(uploadableFile, previewObjectUrl)),
                dimensions,
              );
        const maxWidth = dimensions.maxWidth ?? DEFAULT_IMAGE_DIMENSION_CONSTRAINTS.maxWidth;

        editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
          fileId: res.id,
          fileName: res.name,
          mimeType: uploadableFile.type,
          altText: res.name,
          width,
          height,
          maxWidth,
        });
      } catch (error) {
        onError?.(error instanceof Error ? error : new ImageUploadError('upload'), {
          fileName: file.name,
        });
      }
    }

    return true;
  });

  const handlePaste = useStableCallback((event: ClipboardEvent | InputEvent | KeyboardEvent) => {
    if (event instanceof ClipboardEvent && event.clipboardData?.files) {
      const files = Array.from(event.clipboardData.files);
      if (files.some((file) => getImageFileMimeType(file)?.startsWith('image/'))) {
        event.preventDefault();
        void handleFiles(files);
        return true;
      }
    }

    return false;
  });

  const handleUploadCommand = useStableCallback((payload: ImageUploadCommandPayload) => {
    const files = Array.isArray(payload) ? payload : payload.files;
    const upload = handleFiles(files).then(() => undefined);
    if (!Array.isArray(payload)) {
      payload.onComplete?.(upload);
    }

    void upload;
    return true;
  });

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(PASTE_COMMAND, handlePaste, COMMAND_PRIORITY_LOW),
      editor.registerCommand(UPLOAD_IMAGE_COMMAND, handleUploadCommand, COMMAND_PRIORITY_LOW),
    );
  }, [editor, handlePaste, handleUploadCommand]);

  return null;
}

function getImageFileMimeType(file: File) {
  if (file.type) {
    return file.type;
  }

  const extension = file.name.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'png':
      return 'image/png';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'webp':
      return 'image/webp';
    case 'svg':
      return 'image/svg+xml';
    case 'gif':
      return 'image/gif';
    default:
      return undefined;
  }
}

async function readImageDimensions(file: File, existingObjectUrl?: string) {
  const objectUrl = existingObjectUrl ?? URL.createObjectURL(file);
  const shouldRevokeObjectUrl = !existingObjectUrl;
  try {
    const img = new Image();
    img.src = objectUrl;
    await new Promise<void>((resolve) => {
      img.onload = () => resolve();
      img.onerror = () => resolve();
    });

    return [
      img.naturalWidth || img.width || undefined,
      img.naturalHeight || img.height || undefined,
    ] as const;
  } finally {
    if (shouldRevokeObjectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
  }
}
